import { useDebounceFn } from '@vueuse/core'
import type { SyncableContent, TableKey } from '../../types/database'
import type { RemoteChangePayload } from '../../types/supabase'
import type { Context } from '../../types/content'
import { useContextsStore } from '../stores/contexts'
import { db } from '../services/db'
import { tryCatch } from '../utils/result'
import { resolveUploadAction } from '../utils/syncDecisions'

/** Delay before uploading local changes after the last edit (ms). */
const DEBOUNCE_INTERVAL = 5000
/** Delay before processing batched realtime remote change notifications (ms). */
const CHECK_REMOTE_INTERVAL = 30000
/** Periodic fallback sync interval in case Realtime is unavailable (ms). */
const PERIODIC_SYNC_INTERVAL = 60000

/**
 * Fields that contain user-generated content and must be encrypted before upload.
 * During `transformToSupabase`, these fields are packed into a single
 * `encrypted_data` column and their cleartext columns are set to null,
 * so the server never sees the actual content.
 */
const SENSITIVE_FIELDS: Record<TableKey, string[]> = {
  notes: ['title', 'content', 'preview'],
  tasks: ['title', 'description'],
  lists: ['title', 'items'],
  contexts: ['name'],
  labels: ['name'],
  entries: ['content']
}

/**
 * Composable for bidirectional sync between local IndexedDB and Supabase.
 *
 * Lifecycle:
 * - When a user logs in, a watcher detects the auth state change, waits for
 *   the encryption key to be available, queues all pre-existing local items,
 *   runs a full sync (upload then download), and starts listening for changes.
 * - Local changes (create/update/delete via Dexie hooks) are debounced and
 *   uploaded to Supabase every {@link DEBOUNCE_INTERVAL} ms.
 * - Remote changes are received via Supabase Realtime and debounced with
 *   {@link CHECK_REMOTE_INTERVAL} ms before downloading.
 * - When the user logs out, hooks and realtime subscriptions are cleaned up.
 *
 * Conflict resolution: last-write-wins based on `updatedAt` timestamps.
 *
 * All sensitive fields are encrypted client-side before upload (via
 * `useEncryption`) and decrypted on download. The server only stores opaque
 * ciphertext in the `encrypted_data` column.
 */
export function useSync() {
  const user = useSupabaseUser()
  const supabase = useSupabaseClient()
  const dbOps = useDatabase()
  const {
    encryptPayload,
    decryptPayloadWithFallback,
    hasEncryptionKey,
    getLegacyKey
  } = useEncryption()

  // State
  const isSyncing = ref<boolean>(false)
  const syncError = ref<string | null>(null)
  const isSyncingFromRemote = ref<boolean>(false)
  const isBootstrapping = ref<boolean>(false)

  /**
   * Performs a full sync between the local IndexedDB and the Supabase database.
   * This includes uploading local changes and downloading remote changes.
   *
   * @returns A promise that resolves to an object containing the sync result.
   * The object has the following properties:
   * - success: A boolean indicating whether the sync was successful.
   * - uploaded: The number of local changes uploaded to the Supabase database.
   * - downloaded: The number of remote changes downloaded from the Supabase database.
   * - updated: The number of local items updated as a result of the sync.
   * - error: A string containing the error message if the sync failed.
   */
  async function performFullSync() {
    syncError.value = null
    try {
      // Download first so we know what exists remotely before deciding
      // whether local-only items should be inserted or skipped.
      const downloadResult = await syncRemoteChanges()
      const uploadResult = await syncLocalChanges()
      return {
        success: true,
        uploaded: uploadResult.uploaded,
        downloaded: downloadResult.downloaded,
        updated: downloadResult.updated
      }
    } catch (e) {
      console.error('[sync] Full sync failed:', e)
      syncError.value = e instanceof Error ? e.message : 'sync_failed'
      return { success: false, uploaded: 0, downloaded: 0, updated: 0 }
    }
  }

  /**
   * Uploads all pending local changes to Supabase.
   * Reads the change queue, groups changes by table, uploads each group,
   * then clears only the processed change IDs (new changes queued during
   * the sync are preserved for the next run).
   */
  async function syncLocalChanges(): Promise<{ uploaded: number }> {
    isSyncing.value = true
    try {
      if (!hasEncryptionKey()) throw new Error('encryption_key_missing')

      const pendingChanges = await dbOps.getChanges()
      if (pendingChanges.length === 0) return { uploaded: 0 }

      // Map each change to its table+itemId so we can track which succeeded
      const changesByKey = new Map<string, string[]>()
      for (const change of pendingChanges) {
        const key = `${change.table}:${change.itemId}`
        const ids = changesByKey.get(key) ?? []
        ids.push(change.id!)
        changesByKey.set(key, ids)
      }

      // Order matters: contexts must be uploaded before content tables
      // so that FK constraints (context_id → contexts.id) are satisfied.
      const tables = {
        contexts: new Set<string>(),
        notes: new Set<string>(),
        tasks: new Set<string>(),
        lists: new Set<string>(),
        labels: new Set<string>(),
        entries: new Set<string>()
      }

      for (const change of pendingChanges) {
        tables[change.table].add(change.itemId)
      }

      let totalUploaded = 0
      const succeededChangeIds: string[] = []

      for (const table of Object.keys(tables) as TableKey[]) {
        if (tables[table].size > 0) {
          const result = await uploadEntities(table, Array.from(tables[table]))
          totalUploaded += result.uploaded

          // Only mark successfully processed changes for deletion
          for (const id of tables[table]) {
            const key = `${table}:${id}`
            const status = result.statuses.get(id)
            if (status !== 'error') {
              const ids = changesByKey.get(key) ?? []
              succeededChangeIds.push(...ids)
            }
          }
        }
      }

      // Clear only changes that succeeded (failed ones are preserved for retry)
      if (succeededChangeIds.length > 0) {
        await dbOps.deleteChangesByIds(succeededChangeIds)
      }
      return { uploaded: totalUploaded }
    } finally {
      isSyncing.value = false
    }
  }

  /**
   * Downloads all remote data from Supabase and reconciles with local state.
   * For each table: new remote items are inserted locally, items with a newer
   * remote `updated_at` overwrite local versions, and synced local items that
   * no longer exist remotely are deleted locally.
   */
  async function syncRemoteChanges(): Promise<{
    downloaded: number
    updated: number
  }> {
    isSyncingFromRemote.value = true
    isSyncing.value = true
    try {
      console.log('Starting remote sync')
      if (!hasEncryptionKey()) throw new Error('encryption_key_missing')

      let totalDownloaded = 0
      let totalUpdated = 0
      let totalDecryptionFailures = 0

      // Order matters: contexts must be downloaded before content tables
      // so that FK constraints (context_id → contexts.id) are satisfied.
      const tables: TableKey[] = [
        'contexts',
        'notes',
        'tasks',
        'lists',
        'labels',
        'entries'
      ]
      for (const table of tables) {
        const result = await downloadEntities(table)
        totalDownloaded += result.downloaded
        totalUpdated += result.updated
        totalDecryptionFailures += result.decryptionFailures
        console.log(
          `  ${table}: downloaded ${result.downloaded}, updated ${result.updated}`
        )
      }

      if (totalDecryptionFailures > 0) {
        syncError.value = 'encryption_key_mismatch'
      }

      return { downloaded: totalDownloaded, updated: totalUpdated }
    } finally {
      isSyncingFromRemote.value = false
      isSyncing.value = false
    }
  }

  // ==================== Helpers ====================

  /**
   * Maps local camelCase field names to remote snake_case column names
   * for each syncable table. Used by `transformToSupabase` and `transformFromSupabase`.
   */
  const fieldMapping = {
    contexts: {
      id: 'id',
      name: 'name',
      position: 'position',
      color: 'color',
      createdAt: 'created_at',
      updatedAt: 'updated_at'
    },
    notes: {
      id: 'id',
      contextId: 'context_id',
      title: 'title',
      content: 'content',
      preview: 'preview',
      favourite: 'favourite',
      createdAt: 'created_at',
      updatedAt: 'updated_at'
    },
    tasks: {
      id: 'id',
      contextId: 'context_id',
      title: 'title',
      description: 'description',
      completed: 'completed',
      priority: 'priority',
      dueDate: 'due_date',
      position: 'position',
      createdAt: 'created_at',
      updatedAt: 'updated_at'
    },
    lists: {
      id: 'id',
      contextId: 'context_id',
      title: 'title',
      items: 'items',
      createdAt: 'created_at',
      updatedAt: 'updated_at'
    },
    labels: {
      id: 'id',
      contextId: 'context_id',
      name: 'name',
      createdAt: 'created_at',
      updatedAt: 'updated_at'
    },
    entries: {
      id: 'id',
      contextId: 'context_id',
      content: 'content',
      createdAt: 'created_at',
      updatedAt: 'updated_at'
    }
  }

  /**
   * Transform a local item to a remote item for uploading to Supabase.
   * This function takes a local item and transforms it into a remote item
   * by mapping local fields to remote fields and encrypting sensitive fields.
   *
   * If the local item has a date field that is not null or undefined, the date
   * will be converted to an ISO string for uploading to Supabase.
   *
   * If the local item has sensitive fields, they will be encrypted and stored
   * in the 'encrypted_data' field of the remote item.
   *
   * @param {TableKey} table - The table key of the item
   * @param {SyncableContent} localItem - The local item to be transformed
   * @return {Promise<Record<string, unknown>>} - The transformed remote item
   */
  async function transformToSupabase(
    table: TableKey,
    localItem: SyncableContent
  ) {
    const mapping = fieldMapping[table]
    const sensitiveFields = SENSITIVE_FIELDS[table]
    const remoteItem: Record<string, unknown> = {}
    const sensitiveData: Record<string, unknown> = {}

    for (const [localField, remoteField] of Object.entries(mapping)) {
      const value = (localItem as SyncableContent)[localField]

      if (sensitiveFields.includes(localField)) {
        sensitiveData[remoteField] = value
        remoteItem[remoteField] = null
      } else if (
        isDateField(localField) &&
        value !== null &&
        value !== undefined
      ) {
        remoteItem[remoteField] = new Date(Number(value)).toISOString()
      } else {
        // Convert empty strings to null for UUID fields
        remoteItem[remoteField] =
          value === '' && remoteField.endsWith('_id') ? null : value
      }
    }

    if (Object.keys(sensitiveData).length > 0) {
      remoteItem['encrypted_data'] = await encryptPayload(sensitiveData)
    }

    return remoteItem
  }

  /**
   * Transforms a remote Supabase row back into a local item.
   * Decrypts `encrypted_data` if present, maps snake_case fields to camelCase,
   * and converts ISO date strings back to epoch timestamps.
   *
   * @returns The local item and whether it was decrypted with the legacy key.
   */
  async function transformFromSupabase(
    table: TableKey,
    remoteItem: Record<string, unknown>
  ): Promise<{ data: SyncableContent; wasMigrated: boolean }> {
    const mapping = fieldMapping[table]
    const sensitiveFields = SENSITIVE_FIELDS[table]
    const localItem: Record<string, unknown> = {}

    let decryptedSensitive: Record<string, unknown> = {}
    let wasMigrated = false

    if (
      remoteItem['encrypted_data'] &&
      typeof remoteItem['encrypted_data'] === 'string'
    ) {
      const result = await decryptPayloadWithFallback(
        remoteItem['encrypted_data'] as string
      )
      decryptedSensitive = result.decrypted
      wasMigrated = result.wasMigrated
    }

    for (const [localField, remoteField] of Object.entries(mapping)) {
      let value: unknown
      if (
        sensitiveFields.includes(localField) &&
        decryptedSensitive[remoteField] !== undefined
      ) {
        value = decryptedSensitive[remoteField]
      } else {
        value = remoteItem[remoteField]
      }
      if (isDateField(localField) && value !== null && value !== undefined) {
        localItem[localField] = new Date(value as string).getTime()
      } else {
        localItem[localField] = value
      }
    }

    return { data: localItem as SyncableContent, wasMigrated }
  }

  function isDateField(field: string): boolean {
    return (
      field.endsWith('At') ||
      field.endsWith('_at') ||
      field.endsWith('Date') ||
      field.endsWith('_date')
    )
  }

  /**
   * Uploads a batch of entities from a given table.
   * @param table - The table to upload from.
   * @param ids - The local IDs of the entities to upload.
   */
  async function uploadEntities(table: TableKey, ids: string[]) {
    const results = await Promise.all(
      ids.map((id) => processEntityUpload(table, id))
    )

    const statuses = new Map<string, 'uploaded' | 'skipped' | 'error'>()
    ids.forEach((id, i) => statuses.set(id, results[i] ?? 'error'))

    return {
      uploaded: results.filter((r) => r === 'uploaded').length,
      errors: results.filter((r) => r === 'error').length,
      statuses
    }
  }

  /**
   * Processes the upload of a single entity.
   *
   * Fetches the local entity by ID, compares with the remote version, and
   * decides whether to insert, update, delete, or skip based on timestamps
   * and sync state via {@link resolveUploadAction}.
   *
   * @param table - The table the entity belongs to.
   * @param id - The ID of the entity (same UUID locally and remotely).
   */
  async function processEntityUpload(
    table: TableKey,
    id: string
  ): Promise<'uploaded' | 'skipped' | 'error'> {
    const localItem = await dbOps.getItemById(table, id)
    if (!localItem) return 'skipped'

    const { data: remoteItem, error: fetchError } = (await supabase
      .from(table)
      .select('*')
      .eq('id', id)
      .maybeSingle()) as {
      data: Record<string, unknown> | null
      error: Error | null
    }

    if (fetchError) {
      console.error(fetchError)
      return 'error'
    }

    const remoteUpdatedAt = remoteItem
      ? new Date(remoteItem.updated_at as string).getTime()
      : null

    const action = resolveUploadAction(localItem, !!remoteItem, remoteUpdatedAt)

    if (action === 'skip') return 'skipped'

    if (action === 'mark-synced') {
      await dbOps.markSynced(table, id)
      return 'skipped'
    }

    if (action === 'delete-local') {
      await dbOps.deleteItem(table, id)
      return 'skipped'
    }

    if (action === 'delete-remote') {
      const { error } = await supabase.from(table).delete().eq('id', id)
      if (error) {
        console.error(error)
        return 'error'
      }
      return 'uploaded'
    }

    // 'insert' or 'update'
    const remoteData = await transformToSupabase(table, localItem)

    if (action === 'insert') {
      const { error } = await supabase.from(table).insert(remoteData as never)
      if (error) {
        if (error.code === '23503') {
          // FK constraint violation — parent context no longer exists remotely.
          // Soft-delete the orphaned local item so it gets purged later.
          console.warn(
            `[sync] ${table}/${id}: parent context missing remotely — soft-deleting orphaned item`
          )
          await dbOps.syncUpdate(table, {
            id,
            deletedAt: Date.now(),
            updatedAt: Date.now()
          })
          return 'uploaded'
        }
        console.error(error)
        return 'error'
      }
    } else {
      const { error } = await supabase
        .from(table)
        .update(remoteData as never)
        .eq('id', id)
      if (error) {
        if (error.code === '23503') {
          console.warn(
            `[sync] ${table}/${id}: parent context missing remotely — soft-deleting orphaned item`
          )
          await dbOps.syncUpdate(table, {
            id,
            deletedAt: Date.now(),
            updatedAt: Date.now()
          })
          return 'uploaded'
        }
        console.error(error)
        return 'error'
      }
    }

    await dbOps.markSynced(table, id)
    return 'uploaded'
  }

  /**
   * Downloads all remote entities for a table and reconciles with local state.
   * New remote entities are inserted locally, entities with a newer remote
   * `updated_at` overwrite local versions, and synced local entities that
   * no longer exist remotely are deleted locally.
   *
   * All tables are matched by `id` (same UUID locally and remotely).
   */
  async function downloadEntities(table: TableKey) {
    let downloaded = 0
    let updated = 0
    let deleted = 0

    const { data: remoteEntities, error } = (await supabase
      .from(table)
      .select('*')) as {
      data: Record<string, unknown>[] | null
      error: Error | null
    }

    if (error) {
      console.error(`[sync] Error fetching ${table}:`, error)
      return { downloaded: 0, updated: 0, deleted: 0, decryptionFailures: 0 }
    }

    if (!remoteEntities) {
      console.log(`[sync] ${table}: no remote entities found`)
      return { downloaded: 0, updated: 0, deleted: 0, decryptionFailures: 0 }
    }

    console.log(`[sync] ${table}: ${remoteEntities.length} remote entities`)

    const remoteKeys = new Set(remoteEntities.map((e) => e['id']))

    // 1. Process remote entities (create or update)
    let decryptionFailures = 0

    for (const remoteEntity of remoteEntities) {
      const remoteId = remoteEntity['id'] as string

      try {
        const localItem = (await dbOps.getItemById(table, remoteId)) as
          | SyncableContent
          | undefined

        const { data: transformed, wasMigrated } =
          await transformFromSupabase(table, remoteEntity)

        const remoteUpdatedAt = new Date(
          remoteEntity.updated_at as string
        ).getTime()

        if (!localItem) {
          await dbOps.addItemWithId(table, {
            ...transformed,
            deletedAt: null,
            syncedAt: Date.now()
          } as SyncableContent)
          downloaded++
        } else if (
          remoteUpdatedAt > localItem.updatedAt ||
          localItem.syncedAt === null
        ) {
          await dbOps.syncUpdate(table, {
            ...transformed,
            id: remoteId,
            syncedAt: Date.now()
          })
          updated++
        }

        if (wasMigrated) {
          // Item was decrypted with legacy key — re-queue so it gets
          // re-uploaded encrypted with the current key.
          await queueChange(table, remoteId)
          debouncedSync()
        }
      } catch (entityError) {
        const isDecryptionError =
          entityError instanceof DOMException &&
          entityError.name === 'OperationError'
        const isKeyMissing =
          entityError instanceof Error &&
          entityError.message.includes('No legacy key available')

        if (isDecryptionError || isKeyMissing) {
          const localCopy = await dbOps.getItemById(table, remoteId)
          if (localCopy) {
            // Local copy exists — re-upload with the current encryption key
            // so the remote data becomes readable again. Bump updatedAt so
            // resolveUploadAction returns 'update' instead of 'mark-synced'.
            decryptionFailures++
            console.warn(
              `[sync] ${table}/${remoteId}: remote decryption failed but local copy exists — queuing re-upload`
            )
            await dbOps.syncUpdate(table, { id: remoteId, updatedAt: Date.now() })
            await queueChange(table, remoteId)
          } else {
            // No local copy and undecryptable — orphaned item. Purge it from
            // Supabase so it stops blocking reconciliation on every sync.
            const { error: deleteError } = await supabase
              .from(table)
              .delete()
              .eq('id', remoteId)
            if (deleteError) {
              console.error(
                `[sync] ${table}/${remoteId}: failed to purge orphaned item`,
                deleteError
              )
              decryptionFailures++
            } else {
              console.warn(
                `[sync] ${table}/${remoteId}: purged orphaned item (undecryptable, no local copy)`
              )
              remoteKeys.delete(remoteId)
            }
          }
        } else {
          console.error(
            `[sync] Error processing ${table}/${remoteId}:`,
            entityError
          )
        }
      }
    }

    if (decryptionFailures > 0) {
      console.error(
        `[sync] ${table}: ${decryptionFailures}/${remoteEntities.length} entities failed to decrypt. ` +
          'This indicates an encryption key mismatch — try re-authenticating with your seed phrase.'
      )
    }

    // 2. Delete synced local entities that no longer exist remotely.
    //    Skip entirely when there were decryption failures — we can't
    //    safely determine which local items are obsolete if some remote
    //    items are unreadable.
    if (decryptionFailures === 0) {
      const localItems = await db[table]
        .filter((item) => !item.deletedAt && item.syncedAt !== null)
        .toArray()

      for (const localItem of localItems) {
        if (!remoteKeys.has(localItem.id)) {
          await dbOps.deleteItem(table, localItem.id)
          deleted++
        }
      }
    } else {
      console.warn(
        `[sync] ${table}: skipping local deletion because ${decryptionFailures} remote items could not be decrypted`
      )
    }

    return { downloaded, updated, deleted, decryptionFailures }
  }

  /**
   * Adds a change to the sync queue or updates the timestamp of an existing item if it already exists.
   * @param table - The table of the item to be synced.
   * @param itemId - The ID of the item to be synced.
   */
  async function queueChange(table: TableKey, itemId: string) {
    // Verify if the item is already in the queue
    queueMicrotask(async () => {
      const existing = await dbOps.getChange(table, itemId)

      if (existing) {
        // If it's already in the queue, update the timestamp
        await dbOps.updateChange(existing.id!)
      } else {
        // Add new to queue
        await dbOps.addChange({
          id: crypto.randomUUID(),
          table,
          itemId,
          timestamp: Date.now()
        })
      }
    })
  }

  /**
   * Queues all existing local items for upload.
   * Called before the first fullSync after login to ensure pre-existing
   * local content gets uploaded before the remote download can delete it.
   * Includes soft-deleted items so that offline deletions are propagated.
   *
   * When `resetSyncState` is true, clears `syncedAt` for every item so
   * they are treated as never-synced. This prevents `resolveUploadAction`
   * from returning `delete-local` for items that don't exist remotely
   * under the current ID scheme (e.g. after a slot→ID migration).
   */
  async function queueAllLocalItems(resetSyncState = false) {
    const tables: TableKey[] = [
      'contexts',
      'notes',
      'tasks',
      'lists',
      'labels',
      'entries'
    ]
    for (const table of tables) {
      const items = await db[table].toArray()
      for (const item of items) {
        if (resetSyncState && item.syncedAt !== null) {
          await db[table].update(item.id, { syncedAt: null })
        }
        const existing = await dbOps.getChange(table, item.id)
        if (!existing) {
          await dbOps.addChange({
            id: crypto.randomUUID(),
            table,
            itemId: item.id,
            timestamp: Date.now()
          })
        }
      }
    }
  }

  const debouncedSync = useDebounceFn(async () => {
    const result = await tryCatch(() => syncLocalChanges())
    syncError.value = result.ok ? null : result.error.message
  }, DEBOUNCE_INTERVAL)

  // ==================== Sync Hooks Setup ====================

  /**
   * Subscribes to Dexie CRUD hooks so every local create/update/delete
   * is automatically queued for upload and triggers a debounced sync.
   * Ignores changes that originate from a remote download to avoid loops.
   */
  function setupSyncHooks() {
    dbOps.subscribeToChanges((table, primKey) => {
      if (isSyncingFromRemote.value) return
      queueChange(table, primKey)
      debouncedSync()
    })
  }

  function cleanupSyncHooks() {
    dbOps.unsubscribeFromChanges()
  }

  // ====== Realtime ======

  let realtimeChannel: ReturnType<typeof supabase.channel> | null = null
  let periodicSyncTimer: ReturnType<typeof setInterval> | null = null

  /** Subscribes to Supabase Realtime on all syncable tables. */
  function subscribeToRemoteChanges() {
    unsubscribeFromRemoteChanges()

    realtimeChannel = supabase
      .channel('minimind-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'contexts' },
        handleRemoteChange
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notes' },
        handleRemoteChange
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        handleRemoteChange
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'lists' },
        handleRemoteChange
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'labels' },
        handleRemoteChange
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'entries' },
        handleRemoteChange
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(
            `Realtime subscription ${status}, falling back to periodic sync`
          )
          startPeriodicSync()
        }
        if (status === 'SUBSCRIBED') {
          stopPeriodicSync()
        }
      })

    // Always start periodic sync as a safety net
    startPeriodicSync()
  }

  function unsubscribeFromRemoteChanges() {
    stopPeriodicSync()
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel)
      realtimeChannel = null
    }
  }

  /**
   * Starts a periodic fallback sync to catch remote changes when
   * Realtime is unavailable or unreliable.
   */
  function startPeriodicSync() {
    if (periodicSyncTimer) return
    periodicSyncTimer = setInterval(async () => {
      if (!hasEncryptionKey() || isSyncing.value) return
      const result = await tryCatch(() => syncRemoteChanges())
      if (!result.ok) {
        console.warn('Periodic remote sync failed:', result.error.message)
      }
    }, PERIODIC_SYNC_INTERVAL)
  }

  /** Stops the periodic fallback sync. */
  function stopPeriodicSync() {
    if (periodicSyncTimer) {
      clearInterval(periodicSyncTimer)
      periodicSyncTimer = null
    }
  }

  const debouncedRemoteSync = useDebounceFn(async () => {
    const result = await tryCatch(() => syncRemoteChanges())
    syncError.value = result.ok ? null : result.error.message
  }, CHECK_REMOTE_INTERVAL)

  function handleRemoteChange(_payload: RemoteChangePayload) {
    console.log('Remote change:', _payload)
    debouncedRemoteSync()
  }

  // ==================== Auth-reactive Sync Lifecycle ====================

  /** Whether the sync machinery (hooks + realtime) is currently active. */
  let syncInitialized = false

  /**
   * Tears down all sync listeners (Dexie hooks, Realtime channel,
   * periodic timer). Safe to call even if nothing is active.
   */
  function teardownSync() {
    cleanupSyncHooks()
    unsubscribeFromRemoteChanges()
    syncInitialized = false
  }

  /**
   * Bootstraps the full sync pipeline: queues local items, runs a full
   * bidirectional sync, then starts listening for future changes.
   */
  async function bootstrapSync() {
    if (isBootstrapping.value) return
    isBootstrapping.value = true

    try {
      if (!hasEncryptionKey()) {
        syncError.value = 'encryption_key_missing'
        return
      }

      // Always clean up before (re-)initializing to avoid duplicate listeners
      teardownSync()

      // If a legacy key is present, we're recovering from a key migration.
      // Reset syncedAt so items are treated as new (prevents delete-local
      // for items whose remote ID changed during the slot→ID migration).
      const hasLegacyKey = (await getLegacyKey()) !== null
      await queueAllLocalItems(hasLegacyKey)
      await performFullSync()

      const contextsStore = useContextsStore()
      const existingContexts = await dbOps.getItems<Context>('contexts')
      if (existingContexts.length === 0) {
        const { t } = useI18n()
        const defaultCtx = await contextsStore.addNewContext(
          t('contexts.defaults.life')
        )
        if (defaultCtx) {
          await contextsStore.setCurrentContext(defaultCtx)
        }
      } else if (!contextsStore.currentContext && existingContexts[0]) {
        await contextsStore.setCurrentContext(existingContexts[0])
      }

      setupSyncHooks()
      subscribeToRemoteChanges()
      syncInitialized = true
    } finally {
      isBootstrapping.value = false
    }
  }

  // Start sync when the user logs in. Because the encryption key may be
  // stored slightly after the auth state updates (race condition between
  // the Supabase auth listener and storeKeyInSession), schedule a retry
  // if the key isn't ready yet — hasEncryptionKey is not reactive.
  watch(user, async (newUser) => {
    if (newUser && !syncInitialized) {
      if (hasEncryptionKey()) {
        await bootstrapSync()
      } else {
        setTimeout(async () => {
          if (!syncInitialized && hasEncryptionKey()) {
            await bootstrapSync()
          }
        }, 1000)
      }
    }
  })

  // Teardown on logout: detects user going from truthy to null.
  watch(user, (_newUser, oldUser) => {
    if (!_newUser && oldUser) {
      teardownSync()
    }
  })

  return {
    isSyncing,
    syncError,
    isBootstrapping
  }
}
